import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import LeadCaptureForm from '../components/LeadCaptureForm';
import SplashNav from '../components/SplashNav';
import {
  Search, Zap, FileText, Globe, Mail, BarChart3, Image, BookOpen,
  Wand2, Rss, Link2, Calendar, Users, ArrowRight, Sparkles,
  Shield, Layers, Target, Database, RefreshCw, ChevronLeft, ChevronRight,
  Rocket, TrendingUp, Send, Mic, Video, Tag, MessageSquare, Check,
  Monitor, UserPlus, Megaphone, Headphones, Radar, Bot,
} from 'lucide-react';

const slides = [
  {
    image: 'https://images.pexels.com/photos/6476255/pexels-photo-6476255.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    title: 'Research That Finds What Others Miss',
    desc: 'AI-powered keyword clusters, competitor mapping, and trend analysis — all in one brief.',
    icon: Search,
  },
  {
    image: 'https://images.pexels.com/photos/5387290/pexels-photo-5387290.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    title: 'Generate 8 Content Types at Once',
    desc: 'E-books, email campaigns, ad copy, blog posts, social media, and more — from a single brief.',
    icon: Zap,
  },
  {
    image: 'https://images.pexels.com/photos/261662/pexels-photo-261662.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    title: 'Publish Everywhere in One Click',
    desc: 'Deploy to websites, CMS platforms, social channels, and email sequences simultaneously.',
    icon: Globe,
  },
  {
    image: 'https://images.pexels.com/photos/6476187/pexels-photo-6476187.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    title: 'Track, Analyze, and Optimize',
    desc: 'Real-time analytics across every channel — see what works and double down on winners.',
    icon: BarChart3,
  },
];

const modules = [
  { icon: Search, name: 'Research Hub', desc: 'AI keyword research, competitor analysis, and trend discovery.' },
  { icon: Zap, name: 'Content Factory', desc: 'Generate 8 content types in parallel from one brief.' },
  { icon: FileText, name: 'Content Library', desc: 'Search, filter, and manage all assets with compliance scoring.' },
  { icon: Globe, name: 'Publishing Hub', desc: 'Multi-channel deployment to websites, CMS, and custom endpoints.' },
  { icon: Mail, name: 'Email Marketing', desc: 'Auto-responder sequences, subscriber management, and analytics.' },
  { icon: Image, name: 'Image Generator', desc: 'AI-generated marketing visuals in 10+ styles and multiple dimensions.' },
  { icon: BookOpen, name: 'Cover Generator', desc: 'Professional e-book covers with live preview.' },
  { icon: Wand2, name: 'Prompt Creator', desc: 'Build, save, and optimize reusable AI prompts.' },
  { icon: Rss, name: 'RSS Creator', desc: 'Build and manage RSS feeds for content syndication.' },
  { icon: Link2, name: 'Affiliate Portal', desc: 'Full affiliate program with tiered commission tracking (up to 30% recurring).' },
  { icon: Calendar, name: 'Social Media', desc: 'Schedule posts across LinkedIn, Facebook, Instagram, TikTok.' },
  { icon: Mic, name: 'Media Studio', desc: 'AI podcast generation with 11 voices, AI video creation, branded watermarks, and project management.' },
  { icon: Target, name: 'Lead Capture Builder', desc: 'Branded lead capture pages with custom forms, images, and subscriber management.' },
  { icon: BarChart3, name: 'Analytics', desc: 'Real-time event tracking and performance dashboards.' },
  { icon: Monitor, name: 'Slideshow Builder', desc: 'Interactive demo decks with images, branding, auto-play, and share links.' },
  { icon: Bot, name: 'AI Marketing Manager', desc: 'Set a goal and approve each step before anything goes live.' },
  { icon: Radar, name: 'Lead Generation', desc: 'Find prospects, score fit with AI, and send outreach only after your review.' },
  { icon: Headphones, name: 'Live Support', desc: 'Live chat for your visitors, plus an internal helpdesk with ticket tracking.' },
  { icon: UserPlus, name: 'Team Access', desc: 'Invite team members with admin, editor, and viewer roles.' },
  { icon: Megaphone, name: 'Campaigns Management', desc: 'Group content, emails, social posts, and ads under one campaign goal.' },
  { icon: Users, name: 'SaaS Pricing', desc: 'Subscription tiers, billing, and the affiliate program.' },
];

const assetTypes = [
  { icon: FileText, label: 'E-books' },
  { icon: Mail, label: 'Email Campaigns' },
  { icon: Send, label: 'Ad Copy' },
  { icon: Tag, label: 'SEO Metadata' },
  { icon: FileText, label: 'Blog Posts' },
  { icon: MessageSquare, label: 'Social Posts' },
  { icon: Mic, label: 'Podcast Scripts' },
  { icon: Video, label: 'Video Scripts' },
];

const capabilities = [
  { icon: Shield, title: 'Enterprise Security', desc: 'Encrypted API keys and content that stays private to your account.' },
  { icon: Zap, title: 'Parallel Generation', desc: '8+ content types generated simultaneously from one brief.' },
  { icon: RefreshCw, title: 'Real-Time Sync', desc: 'Live updates across all your devices as you work.' },
  { icon: Layers, title: 'Multi-Channel Publishing', desc: 'Push to websites, social, email, and RSS from one hub.' },
  { icon: Target, title: 'AI Compliance', desc: 'Built-in plagiarism detection via Quetext integration.' },
  { icon: Database, title: 'Secure Cloud Storage', desc: 'Your work is saved safely in the cloud, ready on any device.' },
];

const SplashEngine: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <div className="min-h-screen bg-slate-100">
      <SplashNav />
      {/* Page Header */}
      <section className="bg-gradient-to-br from-violet-600 via-purple-600 to-blue-600 px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-4">
            <Rocket className="h-3.5 w-3.5 text-white" />
            <span className="text-xs font-mono text-white/80 tracking-wider">AURELIAN MARKETING ENGINE</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 tracking-tight">The Marketing Engine</h1>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">Your all-in-one AI content marketing platform — research, generate, publish, and analyze across every channel from a single dashboard.</p>
        </div>
      </section>
      {/* Hero Slideshow */}
      <section className="relative h-[600px] md:h-[680px] overflow-hidden">
        {slides.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              i === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-violet-900/80" />
            <img
              src={slide.image}
              alt={slide.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-violet-900/80" />
            <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-center">
              <div
                className={`max-w-2xl transition-all duration-700 ${
                  i === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
              >
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
                  <slide.icon className="h-3.5 w-3.5 text-violet-300" />
                  <span className="text-xs font-mono text-white/70 tracking-wider">
                    {String(i + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
                  </span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight leading-tight">
                  {slide.title}
                </h2>
                <p className="text-base md:text-lg text-white/70 mb-8 leading-relaxed max-w-xl">
                  {slide.desc}
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-violet-600 to-blue-600 text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:shadow-violet-500/30 transition-all"
                >
                  <Sparkles className="h-4 w-4" />
                  Explore the Platform
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        ))}

        {/* Slide controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* Slide indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`h-2 rounded-full transition-all ${
                i === currentSlide ? 'w-8 bg-violet-400' : 'w-2 bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
            {[
              { value: '21', label: 'Integrated Modules' },
              { value: '8', label: 'Content Types' },
              { value: '5', label: 'Pipeline Stages' },
              { value: 'Up to 30%', label: 'Affiliate Commission' },
              { value: '∞', label: 'Scalable Campaigns' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-3xl md:text-4xl font-bold gradient-text">{stat.value}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-mono mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Grid */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4">
            <Rocket className="h-3.5 w-3.5 text-violet-600" />
            <span className="text-xs font-mono text-slate-500 tracking-wider">PLATFORM MODULES</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">Twenty-One Tools, One Platform</h2>
          <p className="text-sm text-slate-500 max-w-xl mx-auto">Every tool you need for content marketing — research, create, publish, and analyze — working together seamlessly.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {modules.map((m) => (
            <div key={m.name} className="glass-card glass-card-hover p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md">
                  <m.icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800 mb-1">{m.name}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">{m.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Asset Types */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="glass-card p-8 border border-violet-200">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-slate-800 mb-2">Generate 8 Content Types in Parallel</h2>
            <p className="text-sm text-slate-400">From a single research brief, the Content Factory produces all of these simultaneously</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {assetTypes.map((a) => (
              <div key={a.label} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-violet-50 hover:bg-violet-100 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center shadow-md">
                  <a.icon className="h-5 w-5 text-white" />
                </div>
                <span className="text-[10px] text-slate-500 font-mono text-center">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">Enterprise Capabilities</h2>
          <p className="text-sm text-slate-500 max-w-xl mx-auto">Built for scale, security, and performance from the ground up</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {capabilities.map((c) => (
            <div key={c.title} className="glass-card p-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center mb-4 shadow-md">
                <c.icon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-sm font-bold text-slate-800 mb-2">{c.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Lead Capture */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-2xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4">
              <TrendingUp className="h-3.5 w-3.5 text-violet-600" />
              <span className="text-xs font-mono text-slate-500 tracking-wider">EARLY ACCESS</span>
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-3">Start Your Marketing Engine</h2>
            <p className="text-sm text-slate-500 max-w-lg mx-auto">Get exclusive access to the full platform. We'll set up your account and walk you through your first campaign.</p>
          </div>
          <LeadCaptureForm
            pageSource="engine"
            title="Request Platform Access"
            subtitle="Fill out the form below and we'll get you started"
            accentColor="violet"
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-bold text-white">Aurelian Marketing Engine</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/login" className="text-xs text-slate-400 hover:text-white transition-colors">Sign In</Link>
            <Link to="/pricing" className="text-xs text-slate-400 hover:text-white transition-colors">Pricing</Link>
            <Link to="/legal" className="text-xs text-slate-400 hover:text-white transition-colors">Legal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SplashEngine;
